import { useState, useEffect } from 'react';
import axios from 'axios';
import { getThemeColors } from '../themes';

// Use relative URL if REACT_APP_BACKEND_URL is not set (works in production with proxy)
import { getBackendUrl } from '../utils/api';

const API = getBackendUrl() ? `${getBackendUrl()}/api` : '/api';

export const useTheme = (subdomain) => {
  const [theme, setTheme] = useState('default');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (subdomain) {
      loadTheme();
    }
  }, [subdomain]);

  const loadTheme = async () => {
    try {
      setLoading(true);
      console.log('🎨 [useTheme] Loading theme for subdomain:', subdomain);
      console.log('🎨 [useTheme] API URL:', `${API}/public/restaurant/${subdomain}/appearance`);
      
      const response = await axios.get(
        `${API}/public/restaurant/${subdomain}/appearance`
      );
      
      console.log('🎨 [useTheme] Response:', response.data);
      
      const themeName = response.data.theme || 'default';
      const customColors = response.data.custom_colors;
      
      setTheme(themeName);
      
      console.log('🎨 [useTheme] Applying theme:', themeName);
      console.log('🎨 [useTheme] Custom colors from API:', customColors);
      
      // Use colors from API instead of local themes.js
      if (customColors && customColors.primary) {
        applyThemeColors(customColors);
      } else {
        // Fallback to local theme colors
        applyThemeColors(getThemeColors(themeName));
      }
    } catch (error) {
      console.error('🎨 [useTheme] Error loading theme:', error);
      applyThemeColors(getThemeColors('default'));
    } finally {
      setLoading(false);
    }
  };

  const applyThemeColors = (colors) => {
    console.log('🎨 [useTheme] Applying colors:', colors);
    
    // Apply CSS variables to document root
    if (colors && colors.primary) {
      Object.keys(colors).forEach(key => {
        const cssVarName = `--color-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
        document.documentElement.style.setProperty(cssVarName, colors[key]);
        console.log(`🎨 [useTheme] Set ${cssVarName} = ${colors[key]}`);
      });
      
      // Also set Tailwind-compatible variables
      document.documentElement.style.setProperty('--tw-color-primary', colors.primary);
      document.documentElement.style.setProperty('--tw-color-primary-hover', colors.primaryHover || colors.primary);
      
      // Apply inline styles to common elements that use bg-blue or bg-primary
      // This ensures immediate visual feedback
      setTimeout(() => {
        applyInlineStyles(colors);
      }, 100);
      
      console.log('🎨 [useTheme] ✅ All colors applied successfully!');
    } else {
      console.error('🎨 [useTheme] ❌ Invalid colors object:', colors);
    }
  };
  
  const applyInlineStyles = (colors) => {
    console.log('🎨 [useTheme] Applying inline styles as fallback...');
    
    let count = 0;
    
    // Select all buttons with blue/primary background classes
    const selectors = [
      'button[class*="bg-blue"]',
      'button[class*="bg-primary"]',
      'div[class*="bg-blue"]',
      'div[class*="bg-primary"]',
      'span[class*="bg-blue"]',
      'span[class*="bg-primary"]'
    ];
    
    selectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(el => {
        el.style.backgroundColor = colors.primary;
        if (el.tagName === 'BUTTON') {
          el.style.borderColor = colors.primary;
          
          // Add hover effect
          el.addEventListener('mouseenter', function() {
            this.style.backgroundColor = colors.primaryHover || colors.primary;
          });
          el.addEventListener('mouseleave', function() {
            this.style.backgroundColor = colors.primary;
          });
        }
        count++;
      });
    });
    
    console.log(`🎨 [useTheme] ✅ Applied inline styles to ${count} elements`);
  };

  return { theme, loading };
};
